/**
 * The auth port.
 *
 * This is the one place a swappable adapter is the right call rather than the
 * trap: MSW intercepts `fetch`, and Cognito is a redirect flow plus a token
 * endpoint. Everything that IS an HTTP call to our own API goes through
 * services/api/client.ts and is mocked at the network boundary instead.
 *
 * ## Why this interface is redirect-shaped and has no password parameter
 *
 * It used to be `login(email, password)`. That signature is itself the problem,
 * not the implementation behind it: it can only be satisfied by a form in our
 * own JavaScript that handles a user's password. kev-security-model.md is
 * explicit — "The only way to obtain a token is the real Cognito sign-in.
 * Password handling never touches our code — pass 2 uses Hosted UI +
 * authorization code + PKCE, not the browser-side USER_PASSWORD_AUTH the dev
 * prototype used."
 *
 * So the method is gone, and with it the possibility of quietly reintroducing
 * it. `beginSignIn` hands the browser to Cognito's Managed Login and does not
 * return; `completeSignIn` runs on the way back. A password cannot be routed
 * through this interface because there is nowhere to put one.
 *
 * The pool config backs this up rather than relying on convention: the app
 * client sets userPassword, userSrp, custom and adminUserPassword ALL false, so
 * even a correct-looking password call would be refused server-side.
 *
 * ## Both adapters redirect, including the mock
 *
 * The mock could sign in synchronously, and deliberately does not. If it
 * resolved in place while production left the origin, dev would never exercise
 * the boot-restore path — which is the path a paying user hits coming back from
 * Stripe, and the reason `status: "loading"` exists at all. One code path, both
 * environments. This is the same argument the README makes for keeping mocks at
 * the network boundary.
 */

export interface Session {
  userId: string;
  email: string;
  /**
   * Enforced server-side — a pre-authentication trigger denies sign-in when
   * false, and the API re-checks the claim on every request
   * (kev-aws-setup-guide.md §7.2a). Carried here ONLY so the UI can show a
   * resend prompt rather than a generic "sign-in failed".
   */
  emailVerified: boolean;
  /** 18+ self-declaration (§3.3). Gates /app. */
  dobVerified: boolean;
}

/**
 * Captured on OUR page before the redirect, and recorded server-side after the
 * callback returns.
 *
 * Managed Login cannot collect these: it renders Cognito's form, not ours, and
 * a T&C acceptance means nothing without the actual terms linked beside the
 * checkbox. So consent is taken here, carried across the redirect, and written
 * by our API with a SERVER timestamp — which is stronger evidence than a
 * client-supplied time, not weaker.
 *
 * `acceptedTerms` is typed `true`: a signup that has not accepted must not
 * compile. `marketingOptIn` is recorded either way, because under the AU Spam
 * Act "no row" proves nothing — false has to be stored as a value.
 */
export interface Consents {
  /** Literal `true`, not `boolean`. There is no path that records a refusal —
      an account that did not accept simply has no consent row, which is the
      honest representation and the one the API enforces. */
  acceptedTerms: true;
  marketingOptIn: boolean;
  /** Which version of TERMS the punter was actually shown. Recorded beside the
      server timestamp so an audit can answer "to what", not just "when". */
  termsVersion: string;
}

export class AuthError extends Error {
  readonly code: AuthErrorCode;
  constructor(code: AuthErrorCode, message: string) {
    super(message);
    this.name = "AuthError";
    this.code = code;
  }
}

export type AuthErrorCode =
  /**
   * The §7.2a case. Distinct on purpose: the user needs a resend action, and a
   * generic failure would read as a broken login.
   *
   * Note this is what the CLIENT shows. The server must NOT distinguish these
   * in its own responses — that would leak account state, same reasoning as
   * PreventUserExistenceErrors.
   */
  | "email-unverified"
  /** The callback carried no usable code, or `state` did not match what we
      stored. Treated as a hostile callback, never retried automatically. */
  | "callback-invalid"
  | "network"
  | "unknown";

export interface AuthPort {
  /**
   * Called exactly ONCE at boot, before any route guard decides anything.
   *
   * Load-bearing for two reasons:
   *  1. Without it, every page reload flashes the login screen — the single most
   *     common SPA auth bug.
   *  2. The Stripe round-trip is a FULL PAGE LEAVE (window.location.assign). An
   *     in-memory-only token means the user returns from checkout logged out,
   *     immediately after paying. This is the method that fixes that.
   */
  restoreSession(): Promise<Session | null>;

  /**
   * Hand the browser to Managed Login. Never resolves — the page is leaving.
   *
   * `next` is where to land afterwards. It round-trips through the OAuth `state`
   * parameter and MUST be re-validated with safeNext() on the way back: it has
   * been through a URL the user can edit, so it is untrusted on return even
   * though we wrote it.
   */
  beginSignIn(opts: {
    intent: "login" | "signup";
    next: string;
    consents?: Consents;
    /**
     * Skip Cognito's own form and go straight to a federated provider, e.g.
     * "Google". Same authorization-code + PKCE flow and the same callback — the
     * only difference is who collects the credentials.
     *
     * Omitted means Managed Login decides, which is what the email/password
     * doors do. Note a federated user arrives with NO date of birth, country or
     * T&C acceptance: /welcome collects those, and RequireOnboarded already
     * routes anyone without a consent row there.
     */
    idp?: string;
  }): Promise<never>;

  /**
   * Run on /auth/callback. Exchanges the code for tokens, or throws AuthError.
   * Returns the validated destination alongside the session.
   */
  completeSignIn(): Promise<{ session: Session; next: string; consents?: Consents }>;

  logout(): Promise<void>;

  /**
   * ASYNC even where the implementation is synchronous. Real Cognito needs a
   * silent refresh near expiry; a sync signature here would mean rewriting every
   * call site. Returns null when signed out.
   */
  getAccessToken(): Promise<string | null>;

  /**
   * Re-read the session from the identity provider, discarding what we hold.
   *
   * Needed because parts of the session are CLAIMS, and a claim only changes in
   * a token we have not fetched yet. The 18+ declaration is the case that forced
   * it: the API writes `custom:dobVerified` on the account, and the token in
   * this tab still says false — so without a forced refresh the user answers the
   * gate, the write succeeds, and RequireAdult sends them straight back to it.
   *
   * Distinct from the refresh inside getAccessToken(), which fires only near
   * expiry. This one is unconditional, because the caller knows something
   * changed server-side and the expiry clock cannot know that.
   */
  refreshSession(): Promise<Session | null>;

  /** Returns an unsubscribe fn. */
  onSessionChange(cb: (s: Session | null) => void): () => void;

  /** Re-send the verification code (§7.2a frontend contract). */
  resendVerification(email: string): Promise<void>;
}
